import Auth from "@aws-amplify/auth";
import {
  CognitoUserSession,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoAccessToken,
  CognitoUser,
  CognitoUserPool
} from "amazon-cognito-identity-js";
import CONFIG from "../config";
import greetingsName from "../utility/greetingsName";
import { oAuthSignOutSso } from "../utility/oAuthSignout";
import { default as debugLog } from "../utility/logger";
import getSsoSessionStorage from "../utility/mcCookieHandler";

const SSO_TOKEN_NAME = CONFIG.sso.tokenName;
const SSO_HUB_ORIGIN = CONFIG.sso.hubOrigin;

let ssoSession;

class SsoSession {
  constructor() {
    this.ssoSessionStorage = new getSsoSessionStorage(
      SSO_TOKEN_NAME,
      SSO_HUB_ORIGIN
    );
    this.ssoUserDetails = null;
    this.jwtToken = null;
  }

  async syncState({ authState = null, user = null } = {}) {
    const lastAuthEvent = await this.ssoSessionStorage.get("lastAuthEvent");

    if (authState === "signedIn") {
      if (lastAuthEvent === "signIn") {
        await this.storeRefreshToken();
      } else if (lastAuthEvent === "signOut") {
        await this.signOut(user, true);
        await this.clearRefreshToken();
      }
    } else if (authState === "signIn") {
      if (lastAuthEvent === "signIn") {
        if (oAuthSignOutSso.postSignoutCleanup()) {
          await this.clearRefreshToken({ authEvent: "signOut" });
          debugLog("[MC][SSO] Syncing up last 'oAuthSignOut' event");
        } else {
          await this.createSessionFromStoredRefreshToken();
        }
      } else if (lastAuthEvent === "signOut") {
        oAuthSignOutSso.postSignoutCleanup();
      }
    }
  }

  /**
   * Get Cognito User Data or null.
   *
   * Used by SDK Polling
   *
   * TODO: Convert this this to pubsub and re-use across this module
   *       https://github.com/zendesk/cross-storage/pull/41
   */
  async getUser() {
    const lastAuthEvent = await this.ssoSessionStorage.get("lastAuthEvent");

    if (lastAuthEvent === "signIn" && !this.ssoUserDetails) {
      await this.createSessionFromStoredRefreshToken(false);
    } else if (lastAuthEvent === "signOut") {
      this.ssoUserDetails = null;
    }

    return this.ssoUserDetails;
  }

  async storeRefreshToken({ authState = null, authEvent = null } = {}) {
    const currentSession = await Auth.currentSession();

    const storedToken = await this.ssoSessionStorage.getToken();
    const sessionToken = currentSession.refreshToken.token;

    if (storedToken) {
      if (storedToken !== sessionToken) {
        await this.ssoSessionStorage.set(
          SSO_TOKEN_NAME + new Date().toLocaleTimeString(),
          storedToken
        );
        await this.ssoSessionStorage.setToken(sessionToken);
      }
    } else {
      await this.ssoSessionStorage.setToken(sessionToken);
    }
    await this.updateStoredAuthFlow({ authState, authEvent });
  }

  async createSessionFromStoredRefreshToken(
    reloadWhenCreated = true,
    ssoRefreshToken
  ) {
    if (!ssoRefreshToken) {
      ssoRefreshToken = await this.ssoSessionStorage.getToken();

      if (!ssoRefreshToken) {
        return;
      }
    }

    const userPool = new CognitoUserPool({
      UserPoolId: CONFIG.cognito.userPoolId,
      ClientId: CONFIG.cognito.userPoolWebClientId
    });

    const tokens = await fetch(userPool.client.endpoint, {
      headers: {
        "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
        "Content-Type": "application/x-amz-json-1.1"
      },
      mode: "cors",
      cache: "no-cache",
      method: "POST",
      body: JSON.stringify({
        ClientId: userPool.clientId,
        AuthFlow: "REFRESH_TOKEN_AUTH",
        AuthParameters: {
          REFRESH_TOKEN: ssoRefreshToken
        }
      })
    }).then(res => {
      return res.json();
    });

    if (
      !tokens ||
      !tokens.AuthenticationResult ||
      !tokens.AuthenticationResult.IdToken ||
      !tokens.AuthenticationResult.AccessToken
    ) {
      await this.clearRefreshToken();
      return;
    }

    const idToken = tokens.AuthenticationResult.IdToken;
    const accessToken = tokens.AuthenticationResult.AccessToken;
    const cognitoIdToken = new CognitoIdToken({ IdToken: idToken });

    this.jwtToken = cognitoIdToken.jwtToken;
    this.refreshToken = ssoRefreshToken;
    const session = new CognitoUserSession({
      IdToken: new CognitoIdToken({ IdToken: idToken }),
      RefreshToken: new CognitoRefreshToken({ RefreshToken: ssoRefreshToken }),
      AccessToken: new CognitoAccessToken({ AccessToken: accessToken })
    });

    const user = new CognitoUser({
      Username: cognitoIdToken.payload["cognito:username"],
      Pool: userPool
    });
    user.setSignInUserSession(session);
    this.cognitoUser = user;
    this.setSsoUserDetails(cognitoIdToken.payload);
    if (reloadWhenCreated) {
      setTimeout(() => window.location.reload());
    }
  }

  // whitelist cognito data for use in sdk
  setSsoUserDetails(ssoPayload) {
    const userGreetingsName = greetingsName({ attributes: ssoPayload });
    this.ssoUserDetails = {
      username: ssoPayload["cognito:username"],
      uuid: ssoPayload.sub,
      email: ssoPayload.email,
      family_name: ssoPayload.family_name,
      given_name: ssoPayload.given_name,
      display_name: userGreetingsName
    };
  }

  async signOut(user, reload) {
    if (!user || typeof user === "string") {
      try {
        user = this.cognitoUser || (await Auth.currentAuthenticatedUser());
      } catch (e) {
        console.log(e);
      }
    }

    this.ssoUserDetails = null;
    if (user) {
      user.signOut();
      if (reload === true) {
        setTimeout(() => window.location.reload());
      }
    } else {
      setTimeout(() => window.location.reload());
    }
  }

  async clearRefreshToken({ authState = null, authEvent = null } = {}) {
    if (authEvent === "oAuthSignOut") {
      oAuthSignOutSso.preSignoutSetup();
    }
    await this.ssoSessionStorage.deleteToken();
    await this.ssoSessionStorage.delete("ssoActions");
    await this.updateStoredAuthFlow({ authState, authEvent: "signOut" });

    this.ssoUserDetails = null;
  }

  async updateStoredAuthFlow({ authState = null, authEvent = null } = {}) {
    authEvent && (await this.ssoSessionStorage.set("lastAuthEvent", authEvent));
    authState && (await this.ssoSessionStorage.set("lastAuthState", authState));
  }
}

export default function getSsoSesion() {
  if (!ssoSession) {
    ssoSession = new SsoSession();
  }
  return ssoSession;
}
